/**
 * Daily Challenges Helpers
 * Utility functions for shares, calendar, and formatting
 */

import type {
  SharePlatformId,
  CalendarDay,
  CalendarMonth,
  ActivityLevel,
  LevelInfo,
  ShareChallenge,
  LevelTier,
} from '../types';
import {
  SHARE_MESSAGE,
  LEVELS,
  ACTIVITY_THRESHOLDS,
  SHARE_XP_REWARDS,
} from './constants';

// ============================================================================
// Share URL Generators
// ============================================================================

const SHARE_URL = 'https://rumio.world';

export function getShareUrl(platform: SharePlatformId): string {
  const { title, text, hashtags } = SHARE_MESSAGE;
  const encodedUrl = encodeURIComponent(SHARE_URL);
  const encodedText = encodeURIComponent(`${text}`);
  const encodedTitle = encodeURIComponent(title);
  const encodedHashtags = hashtags.join(',');

  switch (platform) {
    case 'twitter':
      return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=${encodedHashtags}`;

    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;

    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;

    case 'whatsapp':
      return `https://wa.me/?text=${encodedText}%20${encodedUrl}`;

    case 'telegram':
      return `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;

    case 'reddit':
      return `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;

    default:
      return SHARE_URL;
  }
}

export function openShareWindow(platform: SharePlatformId): void {
  const url = getShareUrl(platform);
  const width = 600;
  const height = 400;
  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;

  window.open(
    url,
    'share',
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
  );
}

// ============================================================================
// Level Calculations
// ============================================================================

export function getLevelFromXp(xp: number): number {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (xp >= LEVELS[i].xpRequired) {
      return LEVELS[i].level;
    }
  }
  return 1;
}

export function getLevelInfo(xp: number): LevelInfo {
  const level = getLevelFromXp(xp);
  const currentLevelData = LEVELS[level - 1];
  const nextLevelData = level < LEVELS.length ? LEVELS[level] : null;

  const xpForCurrentLevel = currentLevelData.xpRequired;
  const xpForNextLevel = nextLevelData?.xpRequired ?? currentLevelData.xpRequired;

  const xpIntoLevel = xp - xpForCurrentLevel;
  const xpNeededForNext = xpForNextLevel - xpForCurrentLevel;
  const progress = nextLevelData
    ? Math.min(100, Math.round((xpIntoLevel / xpNeededForNext) * 100))
    : 100;

  return {
    level,
    title: currentLevelData.title,
    tier: currentLevelData.tier,
    currentXp: xp,
    xpForCurrentLevel,
    xpForNextLevel,
    progress,
    rewards: currentLevelData.rewards,
  };
}

export function getXpToNextLevel(xp: number): number {
  const level = getLevelFromXp(xp);
  const nextLevel = LEVELS[level];
  if (!nextLevel) return 0;
  return nextLevel.xpRequired - xp;
}

export function getLevelIcon(level: number): string {
  if (level <= 2) return 'Medal';
  if (level <= 4) return 'Ribbon';
  if (level <= 6) return 'Star';
  if (level <= 8) return 'Trophy';
  return 'Diamond';
}

export function getLevelTierColors(tier: LevelTier): { gradient: string; text: string; border: string } {
  switch (tier) {
    case 'bronze':
      return {
        gradient: 'from-tier-bronze to-action-dark',
        text: 'text-tier-bronze',
        border: 'border-tier-bronze',
      };
    case 'silver':
      return {
        gradient: 'from-gray-300 to-gray-500',
        text: 'text-gray-600',
        border: 'border-gray-400',
      };
    case 'gold':
      return {
        gradient: 'from-tier-gold to-action',
        text: 'text-tier-gold',
        border: 'border-tier-gold',
      };
    case 'platinum':
      return {
        gradient: 'from-purple-400 to-purple-600',
        text: 'text-purple-600',
        border: 'border-purple-400',
      };
    case 'diamond':
      return {
        gradient: 'from-cyan-400 to-cyan-600',
        text: 'text-cyan-600',
        border: 'border-cyan-400',
      };
  }
}

// ============================================================================
// Share Challenge Helpers
// ============================================================================

export function getShareChallenges(sharesToday: number): ShareChallenge[] {
  return SHARE_XP_REWARDS.map((xp, index) => ({
    shareNumber: (index + 1) as 1 | 2 | 3,
    xpReward: xp,
    completed: sharesToday > index,
  }));
}

export function getNextShareXp(sharesToday: number): number {
  if (sharesToday >= SHARE_XP_REWARDS.length) return 0;
  return SHARE_XP_REWARDS[sharesToday];
}

export function getTotalShareXpToday(sharesToday: number): number {
  return SHARE_XP_REWARDS.slice(0, sharesToday).reduce((sum, xp) => sum + xp, 0);
}

// ============================================================================
// Calendar Helpers
// ============================================================================

export function getActivityLevel(xpEarned: number): ActivityLevel {
  if (xpEarned <= 0) return 'none';
  if (xpEarned < ACTIVITY_THRESHOLDS.LOW) return 'low';
  if (xpEarned < ACTIVITY_THRESHOLDS.MEDIUM) return 'medium';
  return 'high';
}

export function generateCalendarMonth(
  year: number,
  month: number,
  activityData?: Record<string, number>
): CalendarMonth {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const today = new Date();

  // Get the day of week for the first day (0 = Sunday)
  const startDayOfWeek = firstDay.getDay();

  const days: CalendarDay[] = [];

  // Add days from previous month
  const prevMonthLastDay = new Date(year, month, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
    const date = new Date(year, month - 1, prevMonthLastDay - i);
    days.push({
      date,
      dayOfMonth: prevMonthLastDay - i,
      isCurrentMonth: false,
      isToday: false,
      activityLevel: 'none',
    });
  }

  // Add days from current month
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const date = new Date(year, month, day);
    const dateKey = formatDateKey(date);
    const xpEarned = activityData?.[dateKey] ?? 0;
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    days.push({
      date,
      dayOfMonth: day,
      isCurrentMonth: true,
      isToday,
      activityLevel: getActivityLevel(xpEarned),
      xpEarned,
    });
  }

  // Add days from next month to fill the grid (6 rows x 7 days = 42)
  const remainingDays = 42 - days.length;
  for (let day = 1; day <= remainingDays; day++) {
    const date = new Date(year, month + 1, day);
    days.push({
      date,
      dayOfMonth: day,
      isCurrentMonth: false,
      isToday: false,
      activityLevel: 'none',
    });
  }

  return { year, month, days };
}

export function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month];
}

// ============================================================================
// Formatting Helpers
// ============================================================================

export function formatXp(xp: number): string {
  if (xp >= 1000) {
    return `${(xp / 1000).toFixed(1)}k`;
  }
  return xp.toLocaleString();
}

export function formatStreak(days: number): string {
  if (days === 0) return '0 days';
  if (days === 1) return '1 day';
  return `${days} days`;
}

export function formatBadgeDate(dateString: string | null | undefined): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
